import { Body } from '@assembly-js/design-system';
import clsx from 'clsx';
import React from 'react';

/**
 * Where the control goes once the card is too narrow to fit it beside the
 * label: `below` right-aligns it under the label (buttons, links), `fill`
 * stretches it across the row (selects, text inputs), and `inline` keeps it
 * beside the label (switches, which are small enough to always fit).
 */
type SettingsRowNarrowLayout = 'below' | 'fill' | 'inline';

interface SettingsRowProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  /** The setting's control, right-aligned. Omit for a read-only row. */
  control?: React.ReactNode;
  narrowLayout?: SettingsRowNarrowLayout;
  /** The control's id, so clicking the label focuses it. */
  htmlFor?: string;
  /**
   * `large` sets the row as a card heading, for a row that stands alone as its
   * own card, like the danger zone's delete action.
   */
  size?: 'default' | 'large';
}

export const SettingsRow = ({
  label,
  description,
  control,
  narrowLayout = 'below',
  htmlFor,
  size = 'default',
}: SettingsRowProps) => {
  const isInline = narrowLayout === 'inline';
  const isLarge = size === 'large';
  const LabelTag = htmlFor ? 'label' : 'div';

  return (
    <div
      className={clsx(
        'flex gap-4 px-4 py-3.25',
        isInline
          ? 'items-center'
          : 'flex-col gap-y-2 @[480px]:flex-row @[480px]:items-center',
      )}
    >
      <div className="min-w-0 flex-1">
        <LabelTag htmlFor={htmlFor} className="block">
          <Body
            size="sm"
            tag="span"
            className={clsx(
              'font-medium leading-[22px] text-black-heading',
              isLarge && 'text-[15px]',
            )}
          >
            {label}
          </Body>
        </LabelTag>
        {description && (
          <Body
            size={isLarge ? 'sm' : 'xs'}
            tag="div"
            className="leading-[20px] text-secondary"
          >
            {description}
          </Body>
        )}
      </div>
      {control && (
        <div
          className={clsx(
            'flex shrink-0 items-center',
            narrowLayout === 'below' && 'justify-end',
            narrowLayout === 'fill' && '@max-[480px]:*:w-full',
          )}
        >
          {control}
        </div>
      )}
    </div>
  );
};
